import dayjs, { Dayjs } from "dayjs";

const options: any = {
  hour12: true,
  hour: 'numeric',
  minute: 'numeric',
  separator: '-',
};

export const getDateInFormat = (date: Date): string =>
  date.toLocaleDateString('en-GB', options).split('/').join('-').toUpperCase();

export const decodeString = (string: string): string => {
  return string.replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

export const packObjForSubmit = (obj: any) => {
  const formData = new FormData();
  formData.append("obj", JSON.stringify(obj));
  return formData;
}

export const packObjForSubmitWithAttachments = (obj: any) => {
  const formData = new FormData();
  const objCopy = { ...obj };

  Object.keys(objCopy).forEach((key) => {
    const value = objCopy[key];
    let files: File[] | null = null;
    // handle FileList
    if (value instanceof FileList) files = Array.from(value);
    // handle File[]
    if (Array.isArray(value) && value.every(item => item instanceof File)) files = value;
    // append files
    if (files) {
      files.forEach((file: File) => formData.append("files", file));
      // handle other form data fields
    } else if (value !== null) {
      formData.append(key, value);
    }
  });

  return formData;
};

export const unpackObjForSubmit = (formData: any) => formData.get("obj");

export const capitalizeString = (string: string): string =>
  string.charAt(0).toUpperCase() + string.slice(1);

/**
 * Return true if provided arrays are the same such that
 * * The arrays are of equal length
 * * For each array item, there is an item in the other array at the same index, with the equal value
 * * Array item's are considered equal value, if when serialised as JSON (ex. JSON.stringify), they are equal
 */
export const arraysAreEqual = (arr1: any[], arr2: any[]): boolean => {
  if (arr1.length !== arr2.length) {
    return false;
  }
  for (let i = 0; i < arr1.length; i++) {
    if (JSON.stringify(arr1[i]) !== JSON.stringify(arr2[i])) {
      return false;
    }
  }
  return true;
}

export const getKeyByValue = <T extends object>(Type: T, value: string): keyof T | undefined => {
  const keys = Object.keys(Type) as (keyof T)[];
  return keys.find(key => Type[key] === value);
}

/**
 * Throttles a `fn` so that it only runs once within a `t` milliseconds interval. \
 * Any subsequent calls within this interval will be queued for later. \
 * This is used to prevent unneccessary invocations of `fn`.
 */
export const throttleFn = (fn: Function, t: number = 100) => {
  let timeout: number | null = null;
  return function (this: typeof fn) {
    if (timeout) return;
    const context = this;
    const args = arguments;

    const later = () => {
      fn.call(context, args);
      timeout = null;
    }

    timeout = window.setTimeout(later, t);
  }
}

export const scrollToElement = (elementId: string, offset = 32) => {
  const element = document.getElementById(elementId);
  if (element) {
    const topOffset = element.offsetTop - offset;

    window.scrollTo({
      top: topOffset,
      behavior: 'smooth',
    });
  }
};

export const DATE_FORMAT = 'DD/MM/YYYY';

export const validateDateParts = (day: string, month: string, year: string): boolean => {
  if (!day || !month || !year) return false;
  if (isNaN(Number(day)) || isNaN(Number(month)) || isNaN(Number(year))) return false;
  if (day.length < 1 || day.length > 2) return false;
  if (month.length < 1 || month.length > 2) return false;
  if (year.length !== 4) return false;

  const dayNum = Number(day);
  const monthNum = Number(month);
  const yearNum = Number(year);
  const currentYear = new Date().getFullYear();

  if (monthNum < 1 || monthNum > 12) return false;
  if (yearNum < 1900 || yearNum > currentYear + 10) return false;
  if (dayNum < 1 || dayNum > 31) return false;

  const daysInMonth = new Date(yearNum, monthNum, 0).getDate();
  if (dayNum > daysInMonth) return false;

  return true;
};

export const isValidCompleteDate = (dateString: string | undefined): boolean => {
  if (!dateString || dateString.trim() === '') return false;

  if (dateString.includes('YYYY') || dateString.includes('MM') || dateString.includes('DD')) {
    return false;
  }

  // Only support DD/MM/YYYY format
  if (!dateString.includes('/')) {
    return false;
  }

  const parts = dateString.split('/');
  if (parts.length !== 3) return false;
  const [day, month, year] = parts;

  if (!validateDateParts(day, month, year)) return false;

  const date = dayjs(dateString, DATE_FORMAT, true);
  return date.isValid();
};

/**
 * Parses a date string for use in DatePicker component
 * Returns a Dayjs object if the date is valid and complete, otherwise null
 */
export const parseDateForPicker = (value: string): Dayjs | null => {
  if (!value || !isValidCompleteDate(value)) {
    return null;
  }
  return dayjs(value, DATE_FORMAT, true);
};

export const reformatDate = (
  dateString: string | undefined,
  fromFormat: string = DATE_FORMAT,
  toFormat: string = 'YYYY-MM-DD'
): string => {
  if (!dateString || !isValidCompleteDate(dateString)) {
    return "";
  }
  return dayjs(dateString, fromFormat, true).format(toFormat);
};